import { getBusinessCategorySettingsForCategory } from '@utils/businessCategoryUtils';
import type { AIABillingTerms, BusinessCategory, EstimateState, EstimateType, JobCategories, JobLogType } from '@prisma/client';
import { JobPhase } from '@prisma/client';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorScreen } from '~/components/error-handling/ErrorScreen';
import { LoadingScreen } from '~/components/LoadingScreen';
import type { BillingSettingsCustomer } from '~/components/pages/customers/CustomerBillingSettingsModal';
import { getNextPageParam, useInfinite } from '~/hooks/useInfinite';
import type { JobNavItemId } from '~/layout/jobNavItems';
import type { JobLogClientEntry } from '~/server/schema/jobLog.schema';
import { type SavedPaymentMethod } from '~/utils/paymentProcessor';
import { trpc } from '~/utils/trpc';

export interface IJobState {
	activeLog?: JobLogClientEntry;
	aiaBillingTerms: AIABillingTerms;
	architect?: string;
	projectNumber?: string;
	billingDayOfMonth: number | null;
	businessId: number;
	estimatedBillingPeriods: number | null;
	contractDate?: Date;
	customerBillingSettings: BillingSettingsCustomer;
	customerId: number;
	customerName: string;
	drawerPortalCallbackRef: (node: HTMLDivElement | null) => void;
	drawerPortalElementRef: RefObject<HTMLDivElement | null>;
	drawerOpen: [boolean, Dispatch<SetStateAction<boolean>>];
	isDrawerPortalReady: boolean;
	hasChangeOrders: boolean;
	hiddenJobNavItemIds: JobNavItemId[];
	isAIAProgressBilling: boolean;
	isPickup: boolean;
	jobEstimates: {
		id: number;
		estimateType: EstimateType;
		estimateState: EstimateState;
		version: number | null;
	}[];
	jobId: number;
	internalJobId?: string | null;
	duplicatedFromJobId?: number | null;
	isWarrantyJob?: boolean;
	jobLogFilterTypes: [JobLogType[], Dispatch<SetStateAction<JobLogType[]>>];
	jobLogQuery?: { data: Map<string, JobLogClientEntry[]>; fetchNextPage: () => void; isFetching: boolean; isLoading: boolean; isSuccess: boolean };
	jobName: string;
	jobPhase: JobPhase;
	jobsUseQBClass?: boolean;
	notes: string | null;
	problemReason: string | null;
	flaggedOn: Date | null;
	retainagePercent: number | null;
	retainageCostCodeId: number | null;
	router: NextRouter;
	inWarrantyUntil: string | null;
	warrantyStartDate?: Date | null;
	warrantyDurationDays?: number | null;
	businessCategory: BusinessCategory | null;
	jobCategoryId: number | null;
	category: JobCategories;
	customerPaymentMethods: SavedPaymentMethod[];
	enableInternalJobId?: boolean;
	usesGlazierStudio: boolean;
	usesPartnerPak: boolean;
	usesWinBidPro: boolean;
}

export const JobContext = createContext<IJobState | null>(null);

export const useJobContext = () => {
	const context = useContext(JobContext);
	if (context === null) {
		throw new Error('Job Context must be used within a JobProvider');
	}
	return context;
};

type Props = {
	children: ReactNode;
};

export const JobProvider = (props: Props) => {
	const router = useRouter();
	const { pathname } = router;
	const { jobId } = router.query;
	const drawerRouterQ = router.query.drawerOpen as string;
	const parsedJobId = parseInt(jobId as string);
	const jobIdInt = typeof parsedJobId === 'number' ? parsedJobId : 0;

	const drawerPortalElementRef = useRef<HTMLDivElement>(null);
	const [isDrawerPortalReady, setIsDrawerPortalReady] = useState(false);
	const drawerPortalCallbackRef = useCallback((node: HTMLDivElement | null) => {
		drawerPortalElementRef.current = node;
		setIsDrawerPortalReady(node !== null);
	}, []);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const [jobPhase, setJobPhase] = useState<JobPhase>(JobPhase.ESTIMATE);
	const [activeLog, setActiveLog] = useState<JobLogClientEntry | undefined>(undefined);
	const [jobLogFilterTypes, setJobLogFilterTypes] = useState<JobLogType[]>([]);
	if (jobIdInt !== null && jobIdInt !== undefined) {
		const jobContextQuery = trpc.context.forJob.useQuery({ id: jobIdInt });

		const jobLogQuery = useInfinite(
			trpc.jobLog.jobLogEntriesPaged.useInfiniteQuery(
				{
					jobId: jobIdInt,
					jobLogTypes: jobLogFilterTypes.length > 0 ? jobLogFilterTypes : undefined,
					limit: 50,
				},
				{
					getNextPageParam,
					enabled: pathname === '/jobs/[jobId]',
				}
			)
		);

		const clientEntriesMap = useMemo(() => {
			const map = new Map<string, Array<JobLogClientEntry>>();
			for (const log of jobLogQuery.data) {
				if (map.get(log.date!)) {
					map.get(log.date!)?.push(log);
				} else {
					map.set(log.date!, new Array<JobLogClientEntry>(log));
				}
			}
			return map;
		}, [jobLogQuery.data]);

		useEffect(() => {
			if (jobLogQuery.isSuccess && jobLogQuery.data.length > 0) {
				const latestEntry = jobLogQuery.data[0];
				if (latestEntry !== undefined) {
					setActiveLog(latestEntry);
				}
			}
		}, [jobLogQuery.isSuccess, jobLogQuery.data]);

		//checks for changes in the jobPhase and updates accordingly, so we don't have to invalidate this query anytime the jobPhase changes. Defaults to ESTIMATE
		useEffect(() => {
			if (jobContextQuery.status === 'success' && jobContextQuery.data !== undefined) {
				setJobPhase(jobContextQuery.data.jobPhase);
			}
		}, [jobContextQuery.status, jobContextQuery.data]);

		useEffect(() => {
			if (drawerRouterQ) {
				setDrawerOpen(true);
			} else {
				setDrawerOpen(false);
			}
		}, [drawerRouterQ]);

		if (jobContextQuery.status === 'success' && jobContextQuery.data !== undefined) {
			const { data } = jobContextQuery;

			// Nav items hidden for this job's business category (empty when no category/settings exist).
			const hiddenJobNavItemIds = data.business
				? ((getBusinessCategorySettingsForCategory({
						business: data.business,
						category: data.businessCategory,
						jobCategoryId: data.jobCategoryId,
					}).hiddenJobNavItemIds as JobNavItemId[] | undefined) ?? [])
				: [];

			const jobContextValues: IJobState = {
				activeLog,
				aiaBillingTerms: data.aiaBillingTerms,
				architect: data.architect ?? undefined,
				billingDayOfMonth: data.billingDayOfMonth,
				businessCategory: data.businessCategory,
				businessId: data.businessId,
				jobCategoryId: data.jobCategoryId,
				category: data.category,
				contractDate: data.contractDate ?? undefined,
				customerBillingSettings: data.customer,
				customerId: data.customerId,
				customerName: data.customer.name,
				drawerOpen: [drawerOpen, setDrawerOpen],
				drawerPortalCallbackRef,
				drawerPortalElementRef,
				isDrawerPortalReady,
				hasChangeOrders: data.changeOrders.length > 0,
				hiddenJobNavItemIds,
				inWarrantyUntil: data.inWarrantyUntil,
				isAIAProgressBilling: data.isAIAProgressBilling,
				isPickup: data.isPickup ?? false,
				jobEstimates: data.estimates,
				jobId: data.id,
				internalJobId: data.internalJobId,
				duplicatedFromJobId: data.duplicatedFromJobId,
				estimatedBillingPeriods: data.estimatedBillingPeriods,
				isWarrantyJob: data.isWarrantyJob,
				jobLogFilterTypes: [jobLogFilterTypes, setJobLogFilterTypes],
				jobLogQuery: { ...jobLogQuery, data: clientEntriesMap },
				jobName: data.name,
				jobPhase: jobPhase,
				jobsUseQBClass: data?.business?.jobsUseQBClass,
				notes: data.notes,
				problemReason: data.problemReason,
				flaggedOn: data.flaggedOn,
				projectNumber: data.projectNumber ?? undefined,
				retainagePercent: data.retainagePercent ? +data.retainagePercent : null,
				retainageCostCodeId: data.retainageCostCodeId,
				router,
				warrantyDurationDays: data.warrantyDurationDays,
				warrantyStartDate: data.warrantyStartDate,
				customerPaymentMethods: data.customerPaymentMethods,
				enableInternalJobId: data?.business?.enableInternalJobId,
				usesGlazierStudio: data?.business?.usesGlazierStudio ?? true,
				usesPartnerPak: data?.business?.usesPartnerPak ?? true,
				usesWinBidPro: data?.business?.usesWinBidPro ?? true,
			};

			return <JobContext.Provider value={jobContextValues}>{props.children}</JobContext.Provider>;
		} else if (jobContextQuery.status === 'loading') {
			return <LoadingScreen srText="Loading" />;
		} else {
			return <ErrorScreen srText="Error" message={'Error retrieving Job data.'} />;
		}
	} else {
		return <ErrorScreen srText="Error" message={'Error: Job ID is undefined.'} />;
	}
};
